import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APIProvider, APILoadingStatus, Circle, Map, Marker, useApiLoadingStatus, useMap } from "@vis.gl/react-google-maps";
import { Crosshair, NavigationArrow } from "@phosphor-icons/react";
import { API, trackAnalytics } from "../../lib/api";
import { directionsUrl } from "../../lib/directions";
import { locationErrorMessage, requestLocation } from "../../lib/location";
import { PulseBadge } from "./Discover";

const CENTER = { lat: 29.4241, lng: -98.4936 };
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#050505" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#050505" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#312e81" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#171717" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#2b2138" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3b2d12" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#030712" }] },
];

const PIN_BY_TAG = [
  ["trending", { label: "T", color: "#f5b301", name: "Trending" }],
  ["free_entry", { label: "F", color: "#00ff66", name: "Free Entry" }],
  ["drink_reward", { label: "D", color: "#a855f7", name: "Drink Reward" }],
  ["vip", { label: "V", color: "#f9d976", name: "VIP" }],
  ["live", { label: "L", color: "#8a2be2", name: "Live DJ" }],
];

const LEGEND = PIN_BY_TAG.map(([, pin]) => pin);

function pinFor(venue) {
  const tags = venue.tags || [];
  for (const [tag, pin] of PIN_BY_TAG) {
    if (tags.includes(tag)) return pin;
  }
  return { label: "N", color: "#f5b301", name: "NITEPASS" };
}

function pinIcon(pin) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="21" fill="#050505" fill-opacity="0.94" stroke="${pin.color}" stroke-width="3"/><circle cx="24" cy="24" r="15" fill="${pin.color}" fill-opacity="0.18"/><text x="24" y="30" text-anchor="middle" font-family="Arial,sans-serif" font-size="17" font-weight="900" fill="${pin.color}">${pin.label}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function userIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34"><circle cx="17" cy="17" r="15" fill="#2563eb" fill-opacity="0.22"/><circle cx="17" cy="17" r="8" fill="#2563eb" stroke="#ffffff" stroke-width="4"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function LocationEffect({ location, recenterToken }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !location) return;
    map.panTo({ lat: location.lat, lng: location.lng });
    map.setZoom(Math.max(map.getZoom() || 14, 15));
  }, [map, location, recenterToken]);
  return null;
}

function GoogleNightMap({ venues, onSelect, userLocation, recenterToken }) {
  const [mapFailed, setMapFailed] = useState(false);
  const status = useApiLoadingStatus();
  useEffect(() => {
    if (mapFailed) return undefined;
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => setMapFailed(true);
    const timer = setInterval(() => {
      if (document.querySelector(".gm-err-container")) setMapFailed(true);
    }, 500);
    return () => {
      window.gm_authFailure = previous;
      clearInterval(timer);
    };
  }, [mapFailed]);
  if (mapFailed || status === APILoadingStatus.AUTH_FAILURE || status === APILoadingStatus.FAILED) {
    return (
      <div className="flex h-[calc(100vh-112px)] items-center justify-center px-8 text-center" data-testid="google-map-key-error">
        <div>
          <div className="font-display text-lg font-black text-neon-gold">Google Maps key needs attention</div>
          <p className="mt-2 text-sm leading-relaxed text-zinc-500">The current API key was rejected. Add a valid Maps JavaScript API key with your NITEPASS domains allowed.</p>
        </div>
      </div>
    );
  }
  return (
    <Map
      defaultCenter={CENTER}
      defaultZoom={14}
      styles={DARK_STYLE}
      gestureHandling="greedy"
      disableDefaultUI
      zoomControl
      onError={() => setMapFailed(true)}
      style={{ height: "calc(100vh - 112px)", width: "100%" }}
      data-testid="google-night-map"
    >
      <LocationEffect location={userLocation} recenterToken={recenterToken} />
      {userLocation && (
        <>
          <Marker
            position={{ lat: userLocation.lat, lng: userLocation.lng }}
            title="You are here"
            icon={{ url: userIcon() }}
            zIndex={1000}
          />
          <Circle
            center={{ lat: userLocation.lat, lng: userLocation.lng }}
            radius={Math.max(userLocation.accuracy || 40, 40)}
            strokeColor="#2563eb"
            strokeOpacity={0.45}
            strokeWeight={1}
            fillColor="#2563eb"
            fillOpacity={0.12}
          />
        </>
      )}
      {venues.map((venue) => {
        const pin = pinFor(venue);
        return (
          <Marker
            key={venue.id}
            position={{ lat: venue.lat, lng: venue.lng }}
            title={`${venue.name} — ${pin.name}`}
            icon={{ url: pinIcon(pin) }}
            onClick={() => onSelect(venue)}
          />
        );
      })}
    </Map>
  );
}

export default function NightMap() {
  const [venues, setVenues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [locating, setLocating] = useState(false);
  const [locationResolved, setLocationResolved] = useState(false);
  const [recenterToken, setRecenterToken] = useState(0);
  const navigate = useNavigate();

  const locate = useCallback(async () => {
    setLocating(true);
    setLocationError("");
    try {
      const location = await requestLocation();
      setUserLocation(location);
      setRecenterToken((value) => value + 1);
    } catch (error) {
      setLocationError(locationErrorMessage(error));
    } finally {
      setLocationResolved(true);
      setLocating(false);
    }
  }, []);

  useEffect(() => {
    if (!locationResolved) return;
    const params = new URLSearchParams();
    if (userLocation) {
      params.set("lat", userLocation.lat);
      params.set("lng", userLocation.lng);
      params.set("max_km", "50");
    }
    API.get(`/venues?${params}`).then((r) => setVenues(r.data)).catch(() => {});
  }, [locationResolved, userLocation]);

  useEffect(() => {
    locate();
  }, [locate]);

  return (
    <div data-testid="nite-map-page" className="relative">
      <div className="glass absolute inset-x-4 z-[500] flex items-center justify-between rounded-2xl px-4 py-3"
        style={{ top: "calc(1rem + env(safe-area-inset-top))" }}>
        <div>
          <div className="neon-overline">NITE MAP</div>
          <div className="font-display text-sm font-bold">Where the city is moving</div>
        </div>
        <span className="rounded-full bg-neon-gold/15 px-2.5 py-1 text-[10px] font-bold text-neon-gold">GOOGLE LIVE</span>
      </div>

      {API_KEY ? (
        <APIProvider apiKey={API_KEY}>
          <GoogleNightMap venues={venues} onSelect={(v) => { setSelected(v); trackAnalytics("map_click", v.id); }} userLocation={userLocation} recenterToken={recenterToken} />
        </APIProvider>
      ) : (
        <div className="flex h-[calc(100vh-112px)] items-center justify-center px-8 text-center text-sm text-zinc-500" data-testid="google-map-missing-key">
          Google Maps API key is not configured.
        </div>
      )}

      {locationError && (
        <div className="absolute inset-x-4 top-28 z-[600] rounded-2xl border border-neon-yellow/30 bg-black/85 px-4 py-3 text-xs font-bold text-neon-yellow backdrop-blur" data-testid="location-error">
          {locationError}
        </div>
      )}
      <button data-testid="locate-me-btn" onClick={locate} disabled={locating}
        className="absolute bottom-28 right-4 z-[600] flex h-12 w-12 items-center justify-center rounded-full border border-neon-violet/50 bg-black/85 text-neon-violet shadow-glow-violet backdrop-blur transition-transform duration-150 hover:scale-105 disabled:opacity-50"
        aria-label="Locate me">
        <Crosshair size={22} weight={locating ? "fill" : "bold"} className={locating ? "animate-pulse" : ""} />
      </button>

      <div className="glass absolute inset-x-4 bottom-4 z-[500] rounded-2xl p-3" data-testid="map-legend">
        {selected ? (
          <div className="flex items-center justify-between gap-3" data-testid="map-selected-venue">
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-black">{selected.name}</div>
              <div className="mt-1"><PulseBadge pulse={selected.pulse} label={selected.pulse_label} /></div>
            </div>
            <div className="flex shrink-0 gap-2">
              <a data-testid="map-directions-btn" href={directionsUrl(selected, userLocation)} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 rounded-full border border-neon-violet/40 px-3 py-2 text-xs font-bold text-neon-violet">
                <NavigationArrow size={13} weight="fill" /> Go
              </a>
              <button data-testid="map-close-venue-btn" onClick={() => setSelected(null)} className="rounded-full border border-white/15 px-3 py-2 text-xs font-bold text-zinc-400">Close</button>
              <button data-testid="map-view-venue-btn" onClick={() => navigate(`/venue/${selected.id}`)} className="rounded-full bg-neon-gold px-4 py-2 text-xs font-bold text-black shadow-glow-gold">View</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] font-semibold text-zinc-400">
            {LEGEND.map((pin) => (
              <span key={pin.name} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: pin.color, boxShadow: `0 0 10px ${pin.color}` }} />
                {pin.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
